import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { announcementsApi } from '../../../api/client';
import Modal, { Button, Badge } from '../../ui/Modal';
import { Label, Input, ErrorBanner } from '../../../auth/AuthShell';
import HelpButton from '../../help/HelpButton';

const CATEGORIES = [
  { value: 'whisper',      label: 'Whisper (agent before bridge)' },
  { value: 'ivr_greeting', label: 'IVR greeting' },
  { value: 'hold_prompt',  label: 'Hold / queue position' },
  { value: 'generic',      label: 'Generic' },
];

export default function AnnouncementsPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);
  const [previewingId, setPreviewingId] = useState(null);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ['announcements'],
    queryFn:  () => announcementsApi.list(),
  });
  const { data: voices = [] } = useQuery({
    queryKey: ['announce-voices'],
    queryFn:  announcementsApi.voices,
  });

  const delMut = useMutation({
    mutationFn: announcementsApi.remove,
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });
  const regenMut = useMutation({
    mutationFn: announcementsApi.regenerate,
    onSuccess:  () => qc.invalidateQueries({ queryKey: ['announcements'] }),
  });

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 className="text-xl font-semibold">Announcements</h1>
          <p className="text-sm text-surface-muted dark:text-surface-dark-muted">
            Text-to-speech announcements generated locally with Piper.
            Used as queue whisper files, IVR prompts, hold-position messages, etc.
            All synthesis happens inside the data center — no text leaves.
          </p>
          {voices.length > 0 && (
            <p className="text-xs text-surface-muted mt-1">
              Installed voices: {voices.map(v => v.label).join(' · ')}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <HelpButton moduleKey="admin_announcements" />
          <Button variant="primary" onClick={() => setEditing('new')}>
            + New announcement
          </Button>
        </div>
      </div>

      <div className="rounded-thin border border-surface-border dark:border-surface-dark-border bg-surface-panel dark:bg-surface-dark-panel overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-surface-subtle dark:bg-surface-dark-subtle text-left">
            <tr className="text-xs uppercase tracking-wider text-surface-muted dark:text-surface-dark-muted">
              <th className="px-3 py-2">Name</th>
              <th className="px-3 py-2">Category</th>
              <th className="px-3 py-2">Voice</th>
              <th className="px-3 py-2">Text</th>
              <th className="px-3 py-2">Duration</th>
              <th className="px-3 py-2">Synced</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {isLoading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-surface-muted">Loading…</td></tr>
            )}
            {!isLoading && items.length === 0 && (
              <tr>
                <td colSpan={7} className="px-3 py-10 text-center text-surface-muted">
                  No announcements yet. Click <strong>+ New announcement</strong> to generate your first one.
                </td>
              </tr>
            )}
            {items.map((a) => (
              <tr key={a.id} className="border-t border-surface-border dark:border-surface-dark-border hover:bg-surface-subtle/40 dark:hover:bg-surface-dark-subtle/40">
                <td className="px-3 py-2 font-medium">{a.name}</td>
                <td className="px-3 py-2"><Badge tone="blue">{a.category}</Badge></td>
                <td className="px-3 py-2 text-xs font-mono">{a.voice}</td>
                <td className="px-3 py-2 text-xs max-w-md truncate" title={a.text}>{a.text}</td>
                <td className="px-3 py-2 text-xs">{a.duration_ms ? `${(a.duration_ms/1000).toFixed(1)}s` : '—'}</td>
                <td className="px-3 py-2">
                  {a.last_sync_at ? <Badge tone="green">{new Date(a.last_sync_at).toLocaleTimeString()}</Badge> :
                   a.last_sync_error ? <Badge tone="red" title={a.last_sync_error}>error</Badge> :
                   <Badge tone="amber">pending</Badge>}
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    <Button variant="ghost"
                      onClick={() => {
                        setPreviewingId(a.id);
                        const audio = new Audio(announcementsApi.previewUrl(a.id));
                        audio.play().catch(err => alert('Preview failed: ' + err.message));
                      }}
                    >▶ Preview</Button>
                    <Button variant="ghost" onClick={() => regenMut.mutate(a.id)}>↻ Re-gen</Button>
                    <Button variant="ghost" onClick={() => setEditing(a)}>Edit</Button>
                    <Button variant="danger"
                      onClick={() => {
                        if (confirm(`Delete announcement “${a.name}”? The WAV will be removed from LOU Issabel.`)) {
                          delMut.mutate(a.id);
                        }
                      }}
                    >Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {editing && (
        <AnnouncementEditModal
          announcement={editing === 'new' ? null : editing}
          voices={voices}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['announcements'] }); }}
        />
      )}
    </div>
  );
}

function AnnouncementEditModal({ announcement, voices, onClose, onSaved }) {
  const isNew = !announcement;
  const [form, setForm] = useState(() => ({
    name:     announcement?.name     || '',
    text:     announcement?.text     || '',
    voice:    announcement?.voice    || (voices[0]?.key || ''),
    category: announcement?.category || 'whisper',
    enabled:  announcement ? announcement.enabled : true,
  }));
  const [error, setError] = useState(null);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = useMutation({
    mutationFn: async () => {
      if (!form.name.trim()) throw new Error('Name is required.');
      if (!form.text.trim()) throw new Error('Text is required.');
      if (!form.voice) throw new Error('Voice is required.');
      const payload = {
        name: form.name.trim(),
        text: form.text,
        voice: form.voice,
        category: form.category,
        enabled: !!form.enabled,
      };
      return isNew
        ? announcementsApi.create(payload)
        : announcementsApi.update(announcement.id, payload);
    },
    onSuccess: onSaved,
    onError: (err) => setError(err?.detail || err?.message || 'Save failed.'),
  });
  const onSubmit = (e) => { e.preventDefault(); setError(null); save.mutate(); };

  return (
    <Modal
      open
      onClose={onClose}
      size="lg"
      title={isNew ? 'New announcement' : `Edit “${announcement.name}”`}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" onClick={onSubmit} loading={save.isPending}>
            {isNew ? 'Create & generate' : 'Save (regenerates if text/voice changed)'}
          </Button>
        </>
      }
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <ErrorBanner>{error}</ErrorBanner>
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="name">Name</Label>
            <Input id="name" value={form.name} onChange={(e) => set('name', e.target.value)} required />
          </div>
          <div>
            <Label htmlFor="category">Category</Label>
            <select id="category" value={form.category} onChange={(e) => set('category', e.target.value)}
              className="w-full px-3 py-2 rounded-thin bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border text-sm">
              {CATEGORIES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </div>
        </div>
        <div>
          <Label htmlFor="voice">Voice</Label>
          <select id="voice" value={form.voice} onChange={(e) => set('voice', e.target.value)}
            className="w-full px-3 py-2 rounded-thin bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border text-sm">
            {voices.map((v) => (
              <option key={v.key} value={v.key}>{v.label} — {v.lang}</option>
            ))}
          </select>
        </div>
        <div>
          <Label htmlFor="text">Text</Label>
          <textarea
            id="text"
            value={form.text}
            onChange={(e) => set('text', e.target.value)}
            rows={5}
            required
            placeholder="Type the announcement text. It will be generated as a WAV using the selected voice."
            className="w-full px-3 py-2 rounded-thin bg-surface-base dark:bg-surface-dark-base border border-surface-border dark:border-surface-dark-border text-sm font-mono"
          />
        </div>
        <div className="flex items-center gap-6">
          <label className="flex items-center gap-2 text-sm">
            <input type="checkbox" checked={form.enabled} onChange={(e) => set('enabled', e.target.checked)} />
            Enabled
          </label>
        </div>
        <p className="text-xs text-surface-muted bg-surface-subtle dark:bg-surface-dark-subtle p-2 rounded-thin">
          On save, the WAV is generated locally with Piper and pushed to LOU Issabel automatically.
        </p>
      </form>
    </Modal>
  );
}
